import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import AuthButtons from './AuthButtons';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Badge, Tooltip, IconButton, useMediaQuery } from '@mui/material';
import { ShoppingCart, Menu as MenuIcon, Home as HomeIcon, MusicNote } from '@mui/icons-material';
import { motion } from 'framer-motion';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #030617 0%, #0a1128 100%);
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  padding: 0.75rem 1.5rem;
  background: rgba(10, 17, 40, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;

const Title = styled(motion.h1)`
  color: white;
  margin: 0;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  background: linear-gradient(90deg, #ffffff, #4a90e2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
  
  @media (max-width: 480px) {
    .full-title {
      display: none;
    }
    .short-title {
      display: inline;
    }
  }
`;

const Content = styled.main`
  padding: 2rem;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem 0.75rem;
  }
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const CartButton = styled(motion.button)`
  background: transparent;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 8px;
  border-radius: 50%;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

// Mobile menu component
const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  background: rgba(10, 17, 40, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 1rem;
  z-index: 99;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
`;

const MobileMenuItem = styled(motion.div)`
  padding: 0.75rem;
  color: white;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Footer = styled.footer`
  background: rgba(10, 17, 40, 0.8);
  padding: 1.5rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

const MarketplaceLayout = ({ children }) => {
  const navigate = useNavigate();
  const { cart, getCartItemCount } = useCart();
  const { currentUser, userRoles } = useAuth();
  const [cartItemCount, setCartItemCount] = useState(0);
  const [isArtist, setIsArtist] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:768px)');
  const isSmallMobile = useMediaQuery('(max-width:480px)');
  
  // Update cart item count whenever the cart changes
  useEffect(() => {
    setCartItemCount(getCartItemCount());
  }, [cart, getCartItemCount]);

  // Check if user is an artist
  useEffect(() => {
    if (currentUser && userRoles) {
      setIsArtist(userRoles.ARTIST);
    } else {
      const storedRoles = localStorage.getItem('konzo_user_roles');
      if (storedRoles) {
        try {
          const roles = JSON.parse(storedRoles);
          setIsArtist(roles.ARTIST);
        } catch (error) {
          console.error('Error parsing stored roles:', error);
        }
      }
    }
  }, [currentUser, userRoles]);
  
  const handleCartClick = () => {
    navigate('/marketplace/cart');
    setMobileMenuOpen(false);
  };
  
  const handleTitleClick = () => {
    navigate('/marketplace');
    setMobileMenuOpen(false);
  };
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  const handleNavigate = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };
  
  return (
    <Container>
      <Header>
        <Title 
          onClick={handleTitleClick}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <MusicNote fontSize="small" />
          <span className="full-title">KONZO Marketplace</span>
          <span className="short-title" style={{ display: isSmallMobile ? 'inline' : 'none' }}>KONZO</span>
        </Title>
        
        <NavActions>
          {!isMobile && (
            <>
              <Tooltip title="Shopping Cart">
                <CartButton 
                  onClick={handleCartClick}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Badge badgeContent={cartItemCount} color="primary" max={99}>
                    <ShoppingCart />
                  </Badge>
                </CartButton>
              </Tooltip>
              
              {isArtist && (
                <Button 
                  variant="contained" 
                  color="success" 
                  onClick={() => navigate('/marketplace/withdraw')}
                  sx={{ 
                    mr: 1, 
                    fontWeight: 'bold',
                    '&:hover': { backgroundColor: '#2e7d32' }
                  }}
                >
                  Withdraw Funds
                </Button>
              )}
              
              <AuthButtons />
            </>
          )}
          
          {isMobile && (
            <>
              <Tooltip title="Shopping Cart">
                <CartButton 
                  onClick={handleCartClick}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Badge badgeContent={cartItemCount} color="primary" max={99}>
                    <ShoppingCart />
                  </Badge>
                </CartButton>
              </Tooltip>
              
              <IconButton 
                color="inherit" 
                onClick={toggleMobileMenu}
                sx={{ color: 'white' }}
              >
                <MenuIcon />
              </IconButton>
            </>
          )}
        </NavActions>
      </Header>
      
      {isMobile && mobileMenuOpen && (
        <MobileMenu
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
        >
          <MobileMenuItem 
            onClick={() => handleNavigate('/marketplace')}
            whileTap={{ scale: 0.95 }}
          >
            <HomeIcon /> Home
          </MobileMenuItem>
          
          {isArtist && (
            <MobileMenuItem 
              onClick={() => handleNavigate('/marketplace/withdraw')}
              whileTap={{ scale: 0.95 }}
            >
              <MusicNote /> Withdraw Funds
            </MobileMenuItem>
          )}
          
          <AuthButtons isMobile={true} closeMobileMenu={() => setMobileMenuOpen(false)} />
        </MobileMenu>
      )}
      
      <Content>
        {children}
      </Content>
      
      <Footer>
        © {new Date().getFullYear()} Konzo Marketplace. All rights reserved.
      </Footer>
    </Container>
  );
};

export default MarketplaceLayout;
